import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ControlGroup, Intent, Menu, MenuItem } from "@blueprintjs/core";
import { concat, get, isEmpty, isUndefined } from "lodash";
import { ILayoutProps } from "../types";
import { evaluate } from "components/Template/action";
import { buildPath } from "components/Template/common";
import { isInvalid } from "components/Template/utils";
import { doHidden } from "components/Template/utils";
import { IAnchors, IName, IBase, IMetadata, IList } from "components/Template/types";
import Validate from "components/Template/Validate";
import "./Anchors.scss";

export interface IAnchorsProps extends ILayoutProps {
  template: IAnchors;
  metadata: IMetadata;
  reduceHeight?: boolean;
  scrollContainerSelector?: string; // CSS selector for the scroll container
  aiChangedInputPaths?: Array<string>;
}

const SparklesIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M13 3.5 14.6 8.7 19.8 10.3 14.6 11.9 13 17.1 11.4 11.9 6.2 10.3 11.4 8.7 13 3.5Z"
      fill="currentColor"
    />
    <path
      d="M6.5 13.6 7.4 16.5 10.3 17.4 7.4 18.3 6.5 21.2 5.6 18.3 2.7 17.4 5.6 16.5 6.5 13.6Z"
      fill="currentColor"
    />
  </svg>
);

const validate = (
  initializing: boolean,
  invalid: boolean,
  activated: boolean,
  type: string,
  readOnly: boolean = false
) => {
  const intentType = type === "intent";
  const showActive = (activated: boolean, intentType: any) => {
    if (activated) {
      return intentType ? "primary" : "active";
    } else {
      return intentType ? "none" : "";
    }
  };
  if (readOnly) {
    return showActive(activated, intentType);
  } else {
    if (invalid) {
      if (initializing) {
        return showActive(activated, intentType);
      } else {
        return intentType ? "danger" : "title-fault";
      }
    } else {
      return showActive(activated, intentType);
    }
  }
};

const Anchor = (props: React.PropsWithChildren<IAnchorsProps>) => {
  const { template, model, mutators, result, metadata, scrollContainerSelector = ".model", aiChangedInputPaths = [] } = props;
  const { getValue, isHidden } = mutators;

  const ref = useRef<HTMLDivElement>(null);
  const aiChangedSet = useMemo(() => new Set(aiChangedInputPaths || []), [aiChangedInputPaths]);

  const checkHidden = (v: IBase) => {
    return !((isHidden && isHidden(v.name)) || (v.hidden && evaluate(model, v.hidden, getValue)));
  };
  const anchors = template.items
    .filter((v) => ["group", "list", "tabs", "explorer"].includes(v.type))
    .filter((v) => checkHidden(v));

  const hasAiChanges = useCallback(
    function check(item: IBase, context?: { name: IName; index: number }): boolean {
      const path = context ? buildPath(context.name, context.index, item.name) : item.name;
      switch (item.type) {
        case "explorer":
        case "tab":
        case "group":
        case "tabs":
        case "anchors":
          return (item as any).items?.some((child: IBase) => check(child, context));
        case "list": {
          const listValue = get(model, path) as Array<any>;
          if (!Array.isArray(listValue)) {
            return false;
          }
          return listValue.some((_entry, index) =>
            (item as IList).items.some((child) => check(child, { name: (item as IList).name, index }))
          );
        }
        case "number":
        case "text":
        case "select":
        case "checkbox":
        case "file":
        case "radio":
        case "range":
        case "location":
        case "location_matrix":
        case "calculate":
          return aiChangedSet.has(path);
        default:
          return false;
      }
    },
    [aiChangedSet, model]
  );

  const aiChangedSections = useMemo(() => {
    if (!aiChangedSet.size) {
      return new Set<IName>();
    }
    const next = new Set<IName>();
    anchors.forEach((item) => {
      if (hasAiChanges(item)) {
        next.add(item.name);
      }
    });
    return next;
  }, [anchors, aiChangedSet, hasAiChanges]);

  const [active, setActive] = useState(anchors?.[0].name);

  const handleScroll = useCallback((scrollContainer: Element | Window) => {
    const isActive = (name: IName) => {
      const element = document.getElementById(name);
      
      if (scrollContainer && element) {
        const { top, bottom } = element.getBoundingClientRect();
        
        if (scrollContainer === window) {
          // For window, check if element is in viewport
          const windowHeight = window.innerHeight;
          return (top >= 0 && top <= windowHeight) || (top <= 0 && bottom >= 0);
        } else {
          // For specific container, check if element intersects with container
          const rect = (scrollContainer as HTMLElement).getBoundingClientRect();
          return (rect.top <= top && top <= rect.bottom) || (top <= rect.top && bottom >= rect.bottom);
        }
      }
    };

    const activeItem = anchors.find((v) => isActive(v.name))?.name;
    if (activeItem) {
      setActive(activeItem);
    } else {
      setActive(anchors[anchors.length - 1].name);
    }
  }, [anchors]);

  useEffect(() => {
    // Determine the scroll container (window by default, or specific element)
    const scrollContainer = scrollContainerSelector
      ? document.querySelector(scrollContainerSelector)
      : window;

    if (scrollContainer) {
      const scrollHandler = () => handleScroll(scrollContainer);
      scrollContainer.addEventListener("scroll", scrollHandler);

      return () => {
        scrollContainer.removeEventListener("scroll", scrollHandler);
      };
    }
  }, [handleScroll, scrollContainerSelector]);

  if (doHidden(props)) {
    return null;
  }

  const handleClick = (name: IName) => () => {
    document.getElementsByClassName(`anchor-${name}`)[0]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const renderDivider = (key: string) => <div key={key} className="vertical-line" />;
  const readOnly = isUndefined(mutators.handleFault);
  return (
    <Validate {...props} showLabel showInfo>
      <ControlGroup key={template.name} id={template.name} className={`anchors ${props.reduceHeight ? "reduce-height" : ""}`}>
        <div>
          <Menu>
            {anchors
              .map((v) => {
                const invalid = isInvalid(v, model, result, metadata);
                const activated = v.name === active;
                const isAiChanged = aiChangedSections.has(v.name);
                const itemClassName = [
                  validate(
                    metadata.initializing || !isEmpty(metadata.initialLoading),
                    invalid,
                    activated,
                    "",
                    readOnly
                  ),
                  isAiChanged ? "anchors__item--ai" : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                const labelContent = (
                  <span className={`anchors__label${isAiChanged ? " anchors__label--ai" : ""}`}>
                    <span className="anchors__label-text">{v.label}</span>
                    {isAiChanged && <SparklesIcon className="anchors__label-sparkles" />}
                  </span>
                );
                return (
                  <MenuItem
                    key={v.name}
                    icon="dot"
                    className={itemClassName}
                    onClick={handleClick(v.name)}
                    text={labelContent}
                    intent={
                      validate(
                        metadata.initializing || !isEmpty(metadata.initialLoading),
                        invalid,
                        activated,
                        "intent",
                        readOnly
                      ) as Intent
                    }
                  />
                );
              })
              .reduce(
                (p, v, i, a) => concat(p, i < a.length - 1 ? [v, renderDivider(`divider-${i}`)] : [v]),
                [] as Array<JSX.Element>
              )}
          </Menu>
        </div>
        <div ref={ref}>
          {props.children}
        </div>
      </ControlGroup>
    </Validate>
  );
};

export default Anchor;
